import { useEffect, useMemo, useRef } from 'react';
import { shallow } from 'zustand/shallow';
import { useDepot } from '#~/depot/hooks.js';
import { usePreferences } from '#~/preferences/hooks.js';
import { roomCanControlCacheKey } from '#~/routes/room/boot-readiness.js';
import type { Device } from '@studio/models/device';
import type { Room } from '@studio/models/room';
import {
  StatusEgressState as S,
  StatusEgressReason as R
} from '@tryiris-ai/common.protojs/agent/v1/agent';
import { useGetMachineAgentId } from '#~/agent/components/bridge/hooks.js';
import { locateProxy, peekProxy } from '#~/agent/utils.js';
import {
  ALERT_ACTION,
  createAlertTracker,
  type AlertShowing
} from '#~/analytics/domains/alert-lifecycle.js';
import { useAnalytics } from '#~/analytics/hooks/use-analytics.js';
import { ElectronAPI } from '#~/electron/index.js';
import { isElectronApp } from '#~/electron/utils.js';
import {
  getIncident,
  newShowingId,
  raise,
  subscribeIncidents,
  supersede,
  type IncidentKey
} from '#~/notifications/incidents.js';
import { isPending, post } from '#~/notifications/utils.js';
import { useFeatureFlag } from '#~/posthog/hooks/use-feature-flag.js';
import type { DeviceEgressSelectorType } from '#~/statequery/selectors/device-egress.js';
import { useOutputting } from '#~/room/components/production/output/use-outputting.js';

import { depot } from '#~/depot/index.js';
import {
  openRecordingSavedSession,
  type RecordingBatch,
  type StoppedRecording
} from '../recording-saved-session.js';
import { egressStateName, traceRecordingSaved } from '../recording-saved-trace.js';

/** Own saved results for the authenticated notification panel, across studio navigation. */
export const useRecordingSavedNotices = (accountId: string): void => {
  // A stop is announced only after ACTIVE was observed. At the default 1000ms
  // sampling a short recording's ACTIVE (under a second on the client) was
  // skipped, so the stop read as a startup cancellation and no notice appeared.
  const { devices } = useOutputting({ everyTick: true });
  const roomRecords = useDepot((state) => state.roomRecords);
  // Match the room page's controller gate, including its cached permission while
  // the registry refreshes. A known viewer permission always wins over the cache.
  const cachedControl = usePreferences(
    (preferences) =>
      Object.fromEntries(
        Object.values(devices).map(({ id }) => {
          const roomId = peekProxy(id)?.roomId;
          return [id, roomId ? preferences.getValue(roomCanControlCacheKey(roomId)) : undefined];
        })
      ),
    shallow
  );
  const { agentId: machineAgentId } = useGetMachineAgentId();
  const { enabled, loading } = useFeatureFlag('alert_n6_enabled', { defaultValue: true });
  const n5 = useFeatureFlag('alert_n5_enabled', { defaultValue: true });
  const diskNoticesEnabled = !n5.loading && n5.enabled === true;
  const { track } = useAnalytics();
  const tracker = useMemo(() => createAlertTracker(track), [track]);
  const previous = useRef<Record<string, DeviceEgressSelectorType>>({});
  const active = useRef(new Map<Device['id'], Room['id']>());
  const batches = useRef<RecordingBatch[]>([]);
  const pendingPosts = useRef(new Map<string, RecordingBatch>());
  const persist = useRef<() => void>(() => {});
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const identities = useRef(
    new Map<string, { key: IncidentKey; showing: Omit<AlertShowing, 'showing_id'> }>()
  );

  useEffect(() => {
    const session = openRecordingSavedSession(accountId);
    batches.current = session.snapshot?.batches ?? [];
    traceRecordingSaved('session.open', {
      restoredBatches: batches.current.length,
      restoredHistory: session.snapshot?.history?.length ?? 0
    });
    // History is not a new showing: restore it closed, preserving its timestamp
    // and read state, without replaying a popup or emitting another alert_shown.
    depot.getState().produce((draft) => {
      for (const id of Object.keys(draft.notifications))
        if (id.startsWith('alert:n6:')) delete draft.notifications[id];
      for (const entry of session.snapshot?.history ?? []) {
        draft.notifications[entry.id] = [
          {
            id: entry.id,
            title: entry.title,
            message: entry.message,
            timestamp: new Date(entry.timestamp),
            severity: 'success'
          },
          undefined,
          { isClosed: true, isRead: entry.isRead }
        ];
      }
    });
    persist.current = () =>
      session.save(
        [...batches.current, ...pendingPosts.current.values()],
        depot.getState().notifications
      );
    const unsubscribe = depot.subscribe((next, previous) => {
      if (next.notifications !== previous.notifications) persist.current();
    });
    const pageHide = (event: PageTransitionEvent) => {
      traceRecordingSaved('session.pagehide', {
        persisted: event.persisted,
        liveShowings: identities.current.size
      });
      persist.current();
      if (event.persisted) return;
      unsubscribe();
      persist.current = () => {};
      // A document reload replaces the popup, not its saved history. Close the
      // old showing with its original identity before that document disappears.
      for (const { key } of identities.current.values()) supersede(key);
    };
    window.addEventListener('pagehide', pageHide);
    return () => {
      // Teardown forgets tracked ACTIVE recordings; a stop after this point is unannounced.
      traceRecordingSaved('session.close', {
        activeDevices: active.current.size,
        pendingBatches: batches.current.length,
        liveShowings: identities.current.size
      });
      persist.current();
      persist.current = () => {};
      unsubscribe();
      window.removeEventListener('pagehide', pageHide);
      clearTimeout(timer.current);
      timer.current = undefined;
      batches.current = [];
      pendingPosts.current.clear();
      for (const { key } of identities.current.values()) supersede(key);
      identities.current.clear();
      previous.current = {};
      active.current.clear();
    };
  }, [accountId]);

  // Register after the owner effect so teardown closes live showings before
  // this listener unsubscribes. Tracker changes only replace the listener.
  useEffect(() => {
    const unsubscribe = subscribeIncidents((event) => {
      const { incident } = event;
      if (incident.alert !== 'n6') return;
      const identity = identities.current.get(incident.entity ?? '');
      if (!identity) return;
      const showing = { ...identity.showing, showing_id: incident.showingId };
      if (event.type === 'shown') {
        traceRecordingSaved('notice.shown', { count: identity.showing.recording_count });
        pendingPosts.current.delete(incident.entity!);
        tracker.trackAlertShown(showing);
        persist.current();
      } else if (event.type === 'closed') {
        traceRecordingSaved('notice.closed', { reason: String(event.reason) });
        tracker.trackAlertClosed(showing, event.reason);
        identities.current.delete(incident.entity!);
      } else if (event.action === ALERT_ACTION.SHOW_IN_FOLDER) {
        tracker.trackAlertActed(showing, { action: ALERT_ACTION.SHOW_IN_FOLDER });
      }
    });
    return unsubscribe;
  }, [tracker]);

  // A loading or unset N6 flag silently skips stops, so record each resolution.
  useEffect(() => {
    traceRecordingSaved('flag', { loading, enabled: String(enabled) });
  }, [loading, enabled]);

  useEffect(() => {
    const before = previous.current;
    previous.current = devices;
    const allowed = !loading && enabled === true;
    if (!allowed) {
      clearTimeout(timer.current);
      timer.current = undefined;
      // New transitions still skip an unresolved flag. A restored pending save
      // waits for that flag instead of being lost during reload initialization.
      if (!loading) batches.current = [];
    }
    for (const [entity, { key }] of identities.current) {
      const denied = roomRecords[key.audience as Room['id']]?.permissions.canControl === false;
      const incident = getIncident(key);
      if (((!allowed && !loading) || denied) && incident && isPending(incident.notificationId)) {
        supersede(key);
        identities.current.delete(entity);
        pendingPosts.current.delete(entity);
      }
    }
    const queue = (recording: StoppedRecording) => {
      const now = Date.now();
      let batch = batches.current.find(
        (item) => !item.closed && item.roomId === recording.roomId && now < item.startedAt + 2000
      );
      if (!batch) {
        batch = { roomId: recording.roomId, startedAt: now, recordings: [], closed: false };
        batches.current.push(batch);
      }
      batch.recordings.push(recording);
    };
    const confirmedNow = new Set<Device['id']>();
    const late: StoppedRecording[] = [];
    for (const batch of batches.current) {
      batch.recordings = batch.recordings.filter((recording) => {
        const drop = (reason: string) => {
          traceRecordingSaved('batch.dropped', {
            deviceId: recording.deviceId,
            reason,
            confirmed: recording.confirmed
          });
          return false;
        };
        if (roomRecords[recording.roomId]?.permissions.canControl === false)
          return drop('permission-denied');
        const proxy = locateProxy(recording.deviceId);
        if (proxy && proxy.roomId !== recording.roomId) return drop('proxy-moved-room');
        const status = devices[recording.deviceId]?.egress?.recordingStatus;
        // Leaving a studio disconnects its state source. Keep the pending result:
        // absence cannot revoke a confirmed file or prove an unresolved save.
        if (!status) return true;
        if (recording.confirmed) {
          // A later recording attempt cannot revoke an already confirmed file.
          if (
            status.state === S.EGRESS_STATE_STARTING ||
            status.state === S.EGRESS_STATE_ACTIVE ||
            status.state === S.EGRESS_STATE_FAILED_TO_START
          )
            recording.subsequentAttemptStarted = true;
          return (
            recording.subsequentAttemptStarted ||
            (!status.error &&
              status.state !== S.EGRESS_STATE_FAILED &&
              !(diskNoticesEnabled && status.reason === R.EGRESS_REASON_DISK_FULL)) ||
            drop(`confirmed-then-${egressStateName(status.state)}`)
          );
        }
        if (status.error) return drop('egress-error');
        if (diskNoticesEnabled && status.reason === R.EGRESS_REASON_DISK_FULL)
          return drop('disk-full');
        if (status.state === S.EGRESS_STATE_STOPPING) return true;
        if (status.state !== S.EGRESS_STATE_INACTIVE)
          return drop(`unconfirmed-${egressStateName(status.state)}`);
        recording.confirmed = true;
        confirmedNow.add(recording.deviceId);
        if (batch.closed) {
          late.push(recording);
          return false;
        }
        return true;
      });
    }
    batches.current = batches.current.filter((batch) => batch.recordings.length > 0);
    for (const recording of late) queue(recording);
    for (const id of active.current.keys()) {
      if (devices[id]) continue;
      // useOutputting() returns {} while the machine agent reads offline; a
      // stop observed after this has no ACTIVE history and is not announced.
      traceRecordingSaved('active.dropped', {
        deviceId: id,
        reason: 'device-absent',
        deviceCount: Object.keys(devices).length
      });
      active.current.delete(id);
    }

    for (const current of Object.values(devices)) {
      const deviceId = current.id;
      const proxy = locateProxy(deviceId);
      const roomId = proxy?.roomId;
      const prev = before[deviceId]?.egress?.recordingStatus?.state;
      const status = current.egress?.recordingStatus;
      const now = status?.state;
      if (now === S.EGRESS_STATE_ACTIVE && roomId) active.current.set(deviceId, roomId);
      const wasActive = active.current.get(deviceId) === roomId && !!roomId;
      // A stop RPC can fail and retry through STOPPING without a new ACTIVE tick.
      if (
        now !== S.EGRESS_STATE_ACTIVE &&
        now !== S.EGRESS_STATE_STOPPING &&
        now !== S.EGRESS_STATE_FAILED
      )
        active.current.delete(deviceId);
      const roomControl = roomId ? roomRecords[roomId]?.permissions.canControl : undefined;
      const canControl = roomId ? (roomControl ?? cachedControl[deviceId]) === true : false;
      // Repeated ticks in the same state stay silent; each change logs one line
      // naming the gate that decided it.
      if (prev === now) continue;
      const decide = (decision: string) => {
        traceRecordingSaved('transition', {
          deviceId,
          prev: egressStateName(prev),
          now: egressStateName(now),
          decision,
          roomKnown: !!roomId,
          proxyKnown: !!proxy,
          canControl,
          controlSource:
            roomControl !== undefined
              ? 'room'
              : cachedControl[deviceId] !== undefined
                ? 'cache'
                : 'none',
          wasActive,
          flagLoading: loading,
          flagEnabled: String(enabled),
          hasError: !!status?.error
        });
      };
      if (!roomId || !proxy) {
        decide('skip-no-room');
        continue;
      }
      if (!canControl) {
        decide('skip-no-control');
        continue;
      }
      if (diskNoticesEnabled && status?.reason === R.EGRESS_REASON_DISK_FULL) {
        decide('skip-disk-full');
        continue;
      }

      if (!loading && enabled === false) {
        // Keep the original transition predicate unchanged behind the kill switch.
        if (
          prev !== undefined &&
          prev !== S.EGRESS_STATE_INACTIVE &&
          prev !== S.EGRESS_STATE_FAILED_TO_START &&
          now === S.EGRESS_STATE_INACTIVE
        ) {
          post({
            title: 'Recording stopped',
            message: `${current.name} has stopped recording.`,
            severity: 'success'
          });
        }
        decide('kill-switch');
        continue;
      }
      // STOPPING enrolls the attempt before independently phased encoder polls
      // confirm completion. Direct ACTIVE -> INACTIVE remains supported when
      // state sync skips the intermediate tick. A failed stop retry enrolls on
      // its next STOPPING; FAILED -> INACTIVE alone is an error dismissal.
      const skip = !allowed
        ? 'skip-flag'
        : now !== S.EGRESS_STATE_STOPPING &&
            !(prev === S.EGRESS_STATE_ACTIVE && now === S.EGRESS_STATE_INACTIVE)
          ? 'skip-not-stop'
          : !wasActive
            ? 'skip-not-active'
            : status?.error
              ? 'skip-egress-error'
              : confirmedNow.has(deviceId)
                ? 'skip-already-confirmed'
                : undefined;
      if (skip) {
        decide(skip);
        continue;
      }
      decide('queued');
      queue({
        roomId,
        deviceId,
        name: current.name,
        agentId: proxy.id,
        bridgeName: proxy.name ?? 'Bridge',
        local: isElectronApp() && proxy.id === machineAgentId,
        confirmed: now === S.EGRESS_STATE_INACTIVE,
        subsequentAttemptStarted: false
      });
    }

    if (!allowed) {
      persist.current();
      return;
    }
    const show = (saved: StoppedRecording[]) => {
      // A reload may resolve a different local Bridge; never retain a stale
      // folder capability from the stored snapshot.
      const recordings = saved.map((recording) => ({
        ...recording,
        local: recording.local && isElectronApp() && recording.agentId === machineAgentId
      }));
      const roomId = recordings[0]!.roomId;
      const byBridge = new Map<string, StoppedRecording[]>();
      for (const recording of recordings) {
        const group = byBridge.get(recording.agentId) ?? [];
        group.push(recording);
        byBridge.set(recording.agentId, group);
      }
      const destinations = [...byBridge.values()].map((group) => {
        const first = group[0]!;
        const names = [...new Map(group.map(({ deviceId, name }) => [deviceId, name])).values()];
        return `${names.join(', ')} ${names.length === 1 ? 'was' : 'were'} saved ${first.local ? 'to your recordings folder' : `on ${first.bridgeName}`}.`;
      });
      const count = recordings.length;
      const local = recordings.some((recording) => recording.local);
      // Each completed batch is a distinct result, even when the same camera
      // records again within 60 seconds. Repeated status ticks never queue it twice.
      const entity = `recording:${newShowingId()}`;
      const key = { alert: 'n6', entity, audience: roomId } as const;
      traceRecordingSaved('notice.raised', { count, local, bridges: byBridge.size });
      identities.current.set(entity, {
        key,
        showing: {
          alert_id: 'n6',
          alert_variant: local ? 'local' : 'remote',
          studio_id: roomId,
          // Ed's combined decision: a multi-Bridge result has no single agent.
          ...(byBridge.size === 1 ? { agent_id: recordings[0]!.agentId } : {}),
          recording_count: count
        }
      });
      // Keep the zero-delay post in the reload snapshot until delivery is
      // acknowledged by the real incident helper's shown event.
      pendingPosts.current.set(entity, {
        roomId,
        startedAt: Date.now() - 2000,
        recordings,
        closed: false
      });
      raise(
        key,
        {
          title: count === 1 ? 'Recording saved' : `${count} recordings saved`,
          message: destinations.join(' '),
          severity: 'success'
        },
        {
          variant: local ? 'local' : 'remote',
          ...(local
            ? {
                actions: [
                  {
                    action: ALERT_ACTION.SHOW_IN_FOLDER,
                    label: 'Show in folder',
                    onClick: () => {
                      ElectronAPI.openRecordingPath();
                    }
                  }
                ]
              }
            : {})
        }
      );
    };
    const flush = () => {
      clearTimeout(timer.current);
      timer.current = undefined;
      const now = Date.now();
      for (const batch of batches.current) {
        if (batch.closed || now < batch.startedAt + 2000) continue;
        if (now < batch.startedAt + 10000 && batch.recordings.some((item) => !item.confirmed))
          continue;
        const saved = batch.recordings.filter((item) => item.confirmed);
        traceRecordingSaved('batch.closed', {
          saved: saved.length,
          unconfirmed: batch.recordings.length - saved.length
        });
        if (saved.length > 0) show(saved);
        batch.recordings = batch.recordings.filter((item) => !item.confirmed);
        batch.closed = true;
      }
      batches.current = batches.current.filter((batch) => batch.recordings.length > 0);
      // Membership closes after two seconds; settlement never postpones that
      // boundary. Only unresolved batches need the ten-second deadline timer.
      const deadlines = batches.current
        .filter((batch) => !batch.closed)
        .map((batch) => batch.startedAt + (now < batch.startedAt + 2000 ? 2000 : 10000));
      if (deadlines.length > 0) timer.current = setTimeout(flush, Math.min(...deadlines) - now);
      persist.current();
    };
    flush();
  }, [devices, enabled, loading, roomRecords, cachedControl, machineAgentId, diskNoticesEnabled]);
};
